<template>
  <div class="stats">
    <SideBySide>
      <template #header>
        <div class="tabs control-group">
          <span
            v-for="(value, label) in timeframe"
            :key="label"
            @click="setExtremesWithPreset(value)"
            :class="{ 'active': value == currentTimeframe }"
          >
            {{ label }}
          </span>
        </div>
        <div class="rest-day-toggle-container control-group">
          <input
            type="checkbox"
            id="rest-day-toggle"
            name="rest-day-toggle"
            v-model="hideEmpty"
          >
          <label for="rest-day-toggle">Hide rest days</label>
        </div>
        <div class="control-group">
          <EpButton
            :kind="'naked'"
            :label="true"
            :labelText="'Refresh data'"
            :icon="true"
            :iconSource="'refresh'"
            @click.native="refreshStravaData"
          />
        </div>
      </template>
      <template #left-column>
        <div class="data">
          <section class="overall-stats">
            <p>{{ athlete.firstname }} {{ athlete.lastname }}</p>
            <!-- <h1>{{ getActivityCount }} rides / {{ getMiles(stats.all_ride_totals.distance) }} miles</h1> -->
            <h2>Biggest ride: {{ getMiles(getMaxDistance) }} miles</h2>
            <h2>Fastest speed: {{ getMaxSpeed }} mph</h2>
            <h2>Max wattage: {{ getMaxWattage }} watts</h2>
            <h2>Avg distance: {{ getMiles(getAverageDistance) }} miles</h2>
          </section>
          <section>
            <div class="controls">
              <span>Over time</span>
              <span
                v-for="(series, index) in ['distance', 'average_speed']"
                :key="index"
                @click="currentChartMetric = series"
                :class="['chart-changer', { 'active': series == currentChartMetric }]"
              >
                {{ series }}
              </span>
              </div>
            <EpChart
              :options="chartOptions"
              :extremes="extremes"
              ref="chart"
            />
          </section>
          <section>
            <div class="controls">
              <span>All rides</span>
            </div>
            <EpTable
              :columns="columns"
              :data="filteredActivities"
              :hideEmpty="hideEmpty"
              :selectable="true"
              :selected="selectedRide.id"
              :stickyHeader="true"
              v-on:row-click="updateSelectedRide"
            />
          </section>
        </div>
      </template>
      <template #right-column>
        <div class="selected-ride">
          <div class="selected-ride__stats">
            <p>{{ selectedRide.name }}</p>
            <p>{{ moment(selectedRide.start_date).format('MM/DD/YYYY') }}</p>
            <p>Distance: {{ getMiles(selectedRide.distance) }} miles</p>
            <p>Elevation Gain: {{ getFeet(selectedRide.total_elevation_gain) }} ft</p>
            <p>Moving Time: {{ getHHMMSS(selectedRide.moving_time) }}</p>
            <p>Avg. Power: {{ selectedRide.average_watts }}</p>
            <p>Avg. Speed: {{ getMPH(selectedRide.average_speed) }} mph</p>
            <p>Max. Speed: {{ getMPH(selectedRide.max_speed) }} mph</p>
          </div>
          <EpMap
            :mapZoom="13"
            :mapStyle="'mapbox://styles/ericpitcock/cke3hfy27072i1bmzjovpgvph'"
            :mapSource="mapSource"
            :mapLayer="mapLayer"
            :scrollZoom="true"
            :navigationControl="true"
            :fitToBounds="true"
          />
        </div>
      </template>
    </SideBySide>
  </div>
</template>

<script>
import polyline from '@mapbox/polyline'
import moment from 'moment'
import { getMiles, getMPH, getFeet, getHHMMSS, truncate } from '@/helpers/conversion'
import { mapState, mapGetters, mapActions } from 'vuex'

import EpButton from '@ericpitcock/ep-design-system/src/components/EpButton'
import EpChart from '@ericpitcock/ep-design-system/src/components/EpChart'
import EpMap from '@ericpitcock/ep-design-system/src/components/EpMap'
import EpTable from '@ericpitcock/ep-design-system/src/components/EpTable'

import SideBySide from '@/layouts/SideBySide'

export default {
  data() {
    return {
      currentChartMetric: 'distance',
      currentTimeframe: 30,
      columns: {
        start_date: 'Date',
        name: 'Name',
        distance: 'Distance',
        total_elevation_gain: 'Elevation',
        type: 'Ride Type',
        average_speed: 'Avg Speed',
        average_watts: 'Avg Watts'
      },
      extremesLocal: {
        min: 0,
        max: 0
      },
      hideEmpty: false,
      timeframe: {
        'YTD': 0,
        '60 days': 60,
        '30 days': 30,
        '7 days': 7
      }
    }
  },
  components: {
    EpButton,
    EpChart,
    EpMap,
    EpTable,
    SideBySide
  },
  computed: {
    ...mapGetters([
      'getActivityCount',
      'getAverageDistance',
      'getExtremes',
      'getExtremesMin',
      'getExtremesMax',
      'getMaxDistance',
      'getMaxSpeed',
      'getMaxWattage'
    ]),
    ...mapState([
      'activitiesOverTime',
      'activitiesRaw',
      'athlete',
      'dataLoading',
      'extremes',
      'selectedRide'
    ]),
    chartOptions() {
      return {
        chart: {
          style: {
            fontFamily: 'GT America Condensed Regular'
          },
          margin: 0,
          panning: {
            enabled: true,
            type: 'x'
          },
          panKey: 'shift',
          zoomType: 'x'
        },
        rangeSelector: {
          enabled: false
        },
        title: {
          text: undefined
        },
        xAxis: {
          type: 'datetime',
          crosshair: false,
          events: {
            setExtremes: (event) => {
              this.syncExtremes(event)
            },
            afterSetExtremes: (event) => {
            }
          },
          max: new Date().getTime()
        },
        yAxis: {
          title: {
            enabled: false
          },
          labels: {
            style: {
              color: 'black',
              fontSize: '12px'
            }
          }
        },
        legend: {
          enabled: false
        },
        plotOptions: {
          series: {
            groupPadding: 0,
            point: {
              events: {
                click: (event) => {
                  console.log(event)
                  if (event.point.id === null) return
                  this.updateSelectedRide(event.point.id)
                }
              }
            }
          }
        },
        series: [{
          id: 'distance',
          type: 'column',
          name: 'Distance',
          data: this.chartData(),
          color: 'red',
          states: {
            hover: {
              color: 'purple'
            }
          },
          visible: true
        },
        // {
        //   id: 'average_speed',
        //   type: 'column',
        //   name: 'Speed',
        //   data: this.chartData(),
        //   color: 'red',
        //   states: {
        //     hover: {
        //       color: 'purple'
        //     }
        //   },
        //   visible: true
        // }
        ],
        credits: {
          enabled: false
        },
        exporting: {
          enabled: false
        }
      }
    },
    mapSource() {
      return {
        id: 'test',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: polyline.toGeoJSON(this.selectedRide.map.summary_polyline).coordinates
            }
          }
        }
      }
    },
    mapLayer() {
      return {
        'id': 'test',
        'type': 'line',
        'source': 'test',
        'layout': {
          'line-join': 'round',
          'line-cap': 'round'
        },
        'paint': {
          'line-color': '#4F66DB',
          'line-width': 4
        }
      }
    },
    filteredActivities() {
      return this.tableData().filter(element => {
        const activityTime = moment(element.data.start_date.value).valueOf()
        const min = this.getExtremes.min
        const max = this.getExtremes.max
        return moment(activityTime).isBetween(min, max)
      })
    }
  },
  methods: {
    ...mapActions([
      'refreshStravaData',
      'updateSelectedRide',
      'updateExtremes'
    ]),
    setExtremesWithPreset(days) {
      this.currentTimeframe = days
      let min = undefined
      if (days == 0) {
        min = moment().startOf('year').unix(Number) * 1000
      } else {
        min = moment().subtract(days, 'days').unix(Number) * 1000
      }
      this.updateExtremes({
        min: min,
        max: new Date().getTime()
      })
    },
    syncExtremes(event) {
      this.updateExtremes({ min: event.min, max: event.max })
    },
    chartData() {
      const chartData = []
      let yAxis
      
      this.activitiesOverTime.forEach(element => {
        switch (this.currentChartMetric) {
          case 'distance':
            yAxis = getMiles(element['distance'])
            break;
          case 'average_speed':
            yAxis = getMPH(element['average_speed'])
          default:
            break;
        }
        chartData.push({
          x: moment(element['start_date']).valueOf(),
          y: yAxis,
          id: element['id'] || null,
        })
      })
      return chartData
    },
    tableData() {
      const tableData = []
      this.activitiesOverTime.forEach(element => {
        const {
          id,
          start_date,
          name,
          distance,
          total_elevation_gain,
          type,
          average_speed,
          average_watts } = element
        tableData.push({
          id,
          empty: (name == null) ? true : false,
          data: {
            start_date: {
              value: start_date,
              template: moment(start_date).format('MM/DD/YYYY')
            },
            name: {
              value: name,
              template: truncate(name, 30)
            },
            distance: {
              value: distance,
              template: `${getMiles(distance)} miles`
            },
            total_elevation_gain: {
              value: total_elevation_gain,
              template: `${getFeet(total_elevation_gain)} ft`
            },
            type: {
              value: type,
              template: null
            },
            average_speed: {
              value: average_speed,
              template: `${getMPH(average_speed)} mph`
            },
            average_watts: {
              value: average_watts,
              template: null
            }
          }
        })
      })
      return tableData
    },
    getMiles,
    getMPH,
    getFeet,
    getHHMMSS,
    truncate,
    moment
  },
  watch: {
    getExtremesMin(min) {
      this.extremesLocal.min = min
    },
    getExtremesMax(max) {
      this.extremesLocal.max = max
    },
    extremesLocal: {
      handler(extremes) {
        const chart = this.$refs.chart.chart
        chart.xAxis[0].setExtremes(extremes.min, extremes.max)
      },
      deep: true
    }
  },
  mounted() {
    // console.log(this.tableData())
    // console.log(`Local token expiration: ${new Date(localStorage.getItem('expires_at') * 1000)}`)
    // console.log(`Current time: ${new Date()}`)
  }
}
</script>

<style lang="scss" scoped>
  .header span {
    // margin-right: 30px;
    // &:hover {
    //   cursor: pointer;
    // }
    // &.active {
    //   color: red;
    // }
    &:last-child {
      margin-right: 0;
      margin-left: auto;
    }
  }
  .header .control-group {
    margin-right: 40px;
    input[type="checkbox"] {
      vertical-align: text-top;
      margin: 0;
    }
    label {
      vertical-align: text-top;
    }
  }
  .tabs {
    height: 100%;
    display: flex;
    // justify-content: center;
    // align-items: center;
    // background: yellow;
    span {
      // min-width: 50px;
      display: flex;
      justify-content: center;
      align-items: center;
      // background: red;
      cursor: pointer;
      padding: 2px 5px 1px 5px;
      margin-right: 20px;
      border-bottom: 2px solid transparent;
      &:hover {
        color: red;
      }
      &.active {
        color: red;
        border-bottom: 2px solid red;
      }
    }
  }
  .stats {
    height: 100%;
  }
  .chart-changer {
    cursor: pointer;
    &.active {
      color: red;
    }
  }
  section {
    max-width: 1440px;
    margin: 40px auto 0 auto;
    & + & {
      margin-top: 60px;
    }
  }
  .controls {
    padding: 10px;
    border: 1px solid $medium-gray;
    span + span {
      margin-left: 10px;
    }
  }
  .selected-ride {
    position: relative;
    height: 100%;
    &__stats {
      position: absolute;
      top: 10px;
      left: 10px;
      z-index: 1;
      padding: 20px;
      background: #fff;
    }
  }
</style>
